import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Plus,
  Loader2,
  Wrench,
  ArrowLeft,
  Trash2,
  Image as ImageIcon,
  MessageSquare,
  Send,
  Upload,
  X,
  Building2,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { openProtectedFile } from "@/components/employee/tabHelpers";
import { formatDateHu } from "@/components/DeductionFormDialog";

const Th = ({ children, className = "" }) => (
  <th className={`whitespace-nowrap px-4 py-3 text-left text-[11px] font-medium uppercase tracking-[0.12em] ${className}`}>{children}</th>
);
const Td = ({ children, className = "" }) => (
  <td className={`whitespace-nowrap px-4 py-3 ${className}`}>{children}</td>
);

export default function MaintenancesPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/maintenances");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Betöltés sikertelen.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6 px-4 py-6 sm:px-6 lg:px-8">
      <header>
        <div className="flex items-center gap-1.5 text-muted-foreground/80">
          <Wrench className="h-3.5 w-3.5" strokeWidth={2} />
          <span className="text-[10px] font-semibold uppercase tracking-[0.22em]">Üzemeltetés</span>
        </div>
        <h1 className="mt-1.5 text-2xl font-semibold tracking-tight">Karbantartás</h1>
        <p className="mt-1 text-sm text-muted-foreground">Telephelyi karbantartások, fotódokumentációval és üzenetváltással.</p>
      </header>

      <section className="overflow-hidden rounded-xl border border-border bg-[hsl(var(--sipx-elevated))]">
        <header className="flex flex-col gap-3 border-b border-border px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">Karbantartások</p>
            <p className="mt-0.5 text-sm text-muted-foreground">
              {items.length === 0 ? "Még nincs karbantartás." : `${items.length} karbantartás`}
            </p>
          </div>
          <Button size="sm" onClick={() => setOpenCreate(true)} data-testid="new-maintenance-button">
            <Plus className="h-4 w-4" /> Új karbantartás
          </Button>
        </header>
        {loading ? (
          <div className="flex items-center justify-center px-6 py-14 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Betöltés…
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
            <div className="grid h-10 w-10 place-items-center rounded-full border border-border bg-background">
              <Wrench className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
            </div>
            <p className="text-sm font-medium">Nincs rögzített karbantartás</p>
            <Button variant="outline" size="sm" className="mt-2 h-8" onClick={() => setOpenCreate(true)}>
              <Plus className="h-3.5 w-3.5" /> Új karbantartás
            </Button>
          </div>
        ) : (
          <>
            {/* Mobile card list */}
            <div className="space-y-2 px-3 py-3 md:hidden" data-testid="maintenances-mobile-list">
              {items.map((m) => (
                <button
                  key={m._id}
                  type="button"
                  data-testid={`maintenance-card-${m._id}`}
                  onClick={() => navigate(`/maintenances/${m._id}`)}
                  className="flex w-full flex-col gap-1.5 rounded-2xl border border-border bg-card px-3.5 py-3 text-left transition-colors active:bg-foreground/[0.04]"
                >
                  <p className="line-clamp-2 text-[13.5px] font-medium leading-snug">
                    {m.description}
                  </p>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11.5px] text-muted-foreground">
                    <span className="inline-flex items-center gap-1">
                      <Wrench className="h-3 w-3" strokeWidth={1.75} />
                      {m.site_name}
                    </span>
                    {m.company_name && <span>· {m.company_name}</span>}
                  </div>
                  <div className="mt-0.5 flex items-center justify-between text-[10.5px] font-medium tabular-nums text-muted-foreground/80">
                    <span>{formatDateHu(m.created_at)}</span>
                    <span className="inline-flex items-center gap-2.5">
                      {m.photo_count > 0 && (
                        <span className="inline-flex items-center gap-0.5">
                          <ImageIcon className="h-3 w-3" strokeWidth={1.75} />
                          {m.photo_count}
                        </span>
                      )}
                      {m.comment_count > 0 && (
                        <span className="inline-flex items-center gap-0.5">
                          <MessageSquare className="h-3 w-3" strokeWidth={1.75} />
                          {m.comment_count}
                        </span>
                      )}
                    </span>
                  </div>
                </button>
              ))}
            </div>

            <div className="hidden overflow-x-auto md:block">
              <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30 text-muted-foreground">
                  <Th>Leírás</Th>
                  <Th className="hidden md:table-cell">Cég</Th>
                  <Th>Telephely</Th>
                  <Th className="hidden text-right md:table-cell">Fotók</Th>
                  <Th className="hidden text-right md:table-cell">Üzenetek</Th>
                  <Th className="hidden md:table-cell">Rögzítette</Th>
                  <Th>Időpont</Th>
                </tr>
              </thead>
              <tbody>
                {items.map((m) => (
                  <tr key={m._id} data-testid={`maintenance-row-${m._id}`}
                    onClick={() => navigate(`/maintenances/${m._id}`)}
                    className="cursor-pointer border-b border-border last:border-0 transition-colors hover:bg-foreground/[0.025]">
                    <Td><span className="font-medium line-clamp-2">{m.description}</span></Td>
                    <Td className="hidden text-muted-foreground md:table-cell">{m.company_name}</Td>
                    <Td>{m.site_name}</Td>
                    <Td className="hidden text-right tabular-nums md:table-cell">{m.photo_count}</Td>
                    <Td className="hidden text-right tabular-nums md:table-cell">{m.comment_count}</Td>
                    <Td className="hidden text-muted-foreground md:table-cell">{m.created_by_name}</Td>
                    <Td className="text-muted-foreground tabular-nums">{formatDateHu(m.created_at)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </>
        )}
      </section>

      <CreateMaintenanceDialog open={openCreate} onOpenChange={setOpenCreate} onCreated={(m) => navigate(`/maintenances/${m._id}`)} />
    </div>
  );
}

function CreateMaintenanceDialog({ open, onOpenChange, onCreated }) {
  const [sites, setSites] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [siteId, setSiteId] = useState("");
  const [description, setDescription] = useState("");
  const [photos, setPhotos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    setErr(""); setSiteId(""); setDescription(""); setPhotos([]);
    Promise.all([api.get("/sites"), api.get("/companies")]).then(([s, c]) => {
      setSites(s.data); setCompanies(c.data);
    }).catch(() => {});
  }, [open]);

  const selectedSite = sites.find((s) => s._id === siteId);
  const company = companies.find((c) => c._id === selectedSite?.company_id);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!siteId) return setErr("Telephely kiválasztása kötelező.");
    if (!description.trim()) return setErr("Leírás kötelező.");
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("site_id", siteId);
      fd.append("description", description);
      photos.forEach((p) => fd.append("photos", p));
      const { data } = await api.post("/maintenances", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Karbantartás rögzítve.");
      onCreated(data);
      onOpenChange(false);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Mentés sikertelen.");
    } finally {
      setSaving(false);
    }
  };

  const sortedSites = useMemo(
    () => [...sites].sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [sites]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="maintenance-dialog">
        <DialogHeader>
          <DialogTitle>Új karbantartás</DialogTitle>
          <DialogDescription>Válassz telephelyet és add meg a részleteket.</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-3">
          <label className="block space-y-1.5">
            <span className="block text-xs font-medium text-muted-foreground">Telephely *</span>
            <Select value={siteId} onValueChange={setSiteId}>
              <SelectTrigger data-testid="maintenance-site-trigger"><SelectValue placeholder="Válassz telephelyet…" /></SelectTrigger>
              <SelectContent>
                {sortedSites.map((s) => (<SelectItem key={s._id} value={s._id}>{s.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </label>
          {company && (
            <p className="rounded-md border border-border bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
              <Building2 className="mr-1 inline h-3 w-3" /> Cég (automatikus): <b className="text-foreground">{company.name}</b>
            </p>
          )}
          <label className="block space-y-1.5">
            <span className="block text-xs font-medium text-muted-foreground">Leírás *</span>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              data-testid="maintenance-desc"
              placeholder="Mi a probléma vagy elvégzendő feladat?"
            />
          </label>
          <label className="block space-y-1.5">
            <span className="block text-xs font-medium text-muted-foreground">Fotók</span>
            <Input
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => setPhotos(Array.from(e.target.files || []))}
              data-testid="maintenance-photos"
            />
            {photos.length > 0 && (
              <p className="text-[11px] text-muted-foreground">{photos.length} fájl kiválasztva</p>
            )}
          </label>
          {err && <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">{err}</div>}
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Mégse</Button>
            <Button type="submit" disabled={saving} data-testid="maintenance-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Rögzítés
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function MaintenanceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [m, setM] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/maintenances/${id}`);
      setM(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Betöltés sikertelen.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const onPostComment = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setSending(true);
    try {
      await api.post(`/maintenances/${id}/comments`, { message, author_role: "maintenance" });
      setMessage("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Nem sikerült üzenetet küldeni.");
    } finally {
      setSending(false);
    }
  };

  const onUploadPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("photos", f));
      await api.post(`/maintenances/${id}/photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Fotók feltöltve.");
      e.target.value = "";
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Feltöltés sikertelen.");
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async () => {
    if (!window.confirm("Törlöd ezt a karbantartást?")) return;
    try {
      await api.delete(`/maintenances/${id}`);
      toast.success("Karbantartás törölve.");
      navigate("/maintenances");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Nem sikerült törölni.");
    }
  };

  if (loading || !m) {
    return (
      <div className="flex items-center justify-center px-6 py-14 text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Betöltés…
      </div>
    );
  }

  return (
    <div className="space-y-6 px-4 py-6 sm:px-6 lg:px-8" data-testid="maintenance-detail-page">
      <button onClick={() => navigate("/maintenances")} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition hover:text-foreground">
        <ArrowLeft className="h-3.5 w-3.5" /> Karbantartások
      </button>

      {/* Hero card */}
      <section className="relative overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-amber-50/50 via-white to-orange-50/30 p-6 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_-12px_rgba(245,158,11,0.18)]">
        <div aria-hidden className="pointer-events-none absolute -top-16 -right-16 h-44 w-44 rounded-full bg-amber-200/30 blur-3xl" />
        <div aria-hidden className="pointer-events-none absolute -bottom-20 -left-12 h-44 w-44 rounded-full bg-orange-200/20 blur-3xl" />

        <div className="relative flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white shadow-sm ring-1 ring-amber-200">
              <Wrench className="h-5 w-5 text-amber-700" strokeWidth={2} />
            </div>
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-amber-800/80">
                Karbantartás
              </div>
              <h1 className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">{m.site_name}</h1>
              <p className="mt-0.5 inline-flex items-center gap-1.5 text-sm text-slate-600">
                <Building2 className="h-3.5 w-3.5 text-slate-400" />
                {m.company_name}
              </p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onDelete} className="text-rose-600 hover:bg-rose-50 hover:text-rose-700">
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>

        <div className="relative mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Stat label="Bejelentő" value={m.created_by_name} />
          <Stat label="Időpont" value={formatDateHu(m.created_at)} />
          <Stat label="Fotók" value={(m.photos || []).length} mono />
          <Stat label="Üzenetek" value={(m.timeline || []).length} mono />
        </div>
      </section>

      {/* Description */}
      <section className="rounded-2xl border border-border bg-card p-6 shadow-sm">
        <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Leírás</div>
        <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-slate-800">{m.description}</p>
      </section>

      {/* Photos */}
      <section className="rounded-2xl border border-border bg-card shadow-sm">
        <header className="flex items-center justify-between border-b border-border px-5 py-4">
          <div className="flex items-center gap-2">
            <ImageIcon className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Fotók
            </span>
            <span className="text-xs text-muted-foreground">· {(m.photos || []).length}</span>
          </div>
          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-input bg-background px-2.5 py-1.5 text-xs font-medium hover:bg-muted">
            <Upload className="h-3 w-3" /> {uploading ? "Feltöltés…" : "Új fotók"}
            <input type="file" accept="image/*" multiple className="hidden" onChange={onUploadPhotos} disabled={uploading} data-testid="upload-photos" />
          </label>
        </header>
        {(m.photos || []).length === 0 ? (
          <p className="px-5 py-10 text-center text-xs text-muted-foreground">Nincs feltöltött fotó.</p>
        ) : (
          <div className="grid grid-cols-2 gap-3 p-5 sm:grid-cols-4 lg:grid-cols-6">
            {m.photos.map((p, idx) => (
              <button
                key={idx}
                onClick={() => openProtectedFile(`/maintenances/${id}/photos/${idx}`, p.content_type, p.name)}
                data-testid={`photo-${idx}`}
                className="group relative overflow-hidden rounded-xl border border-border bg-muted/20 transition hover:border-amber-300 hover:shadow-md"
              >
                <div className="flex aspect-square items-center justify-center bg-gradient-to-br from-amber-50/40 to-orange-50/30 text-amber-600/70">
                  <ImageIcon className="h-6 w-6" />
                </div>
                <p className="truncate border-t border-border bg-background/80 px-2 py-1 text-[10.5px] text-muted-foreground">{p.name}</p>
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Timeline */}
      <section className="rounded-2xl border border-border bg-card shadow-sm">
        <header className="flex items-center gap-2 border-b border-border px-5 py-4">
          <MessageSquare className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            Idővonal
          </span>
          <span className="text-xs text-muted-foreground">· {(m.timeline || []).length}</span>
        </header>
        <div className="space-y-3 px-5 py-5">
          {(m.timeline || []).length === 0 ? (
            <p className="py-6 text-center text-xs text-muted-foreground">Még nincs üzenet az idővonalon.</p>
          ) : (
            m.timeline.map((t) => (
              <div key={t._id} data-testid={`comment-${t._id}`} className="flex items-start gap-3">
                <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-gradient-to-br from-slate-100 to-slate-200 text-[10px] font-semibold uppercase tracking-wider text-slate-600">
                  {(t.author_name || "?").slice(0, 2).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1 rounded-xl border border-border bg-muted/20 px-3.5 py-2.5">
                  <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                    <span className="font-semibold text-slate-700">{t.author_name}</span>
                    <span>·</span>
                    <span className="tabular-nums">{formatDateHu(t.created_at)}</span>
                  </div>
                  <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-slate-800">{t.message}</p>
                </div>
              </div>
            ))
          )}
        </div>
        <form onSubmit={onPostComment} className="flex items-end gap-2 border-t border-border px-5 py-4">
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={2}
            placeholder="Írj üzenetet az idővonalra…"
            data-testid="comment-input"
            className="flex-1"
          />
          <Button type="submit" disabled={sending || !message.trim()} data-testid="comment-submit" className="gap-1.5 bg-slate-900 text-white hover:bg-slate-800">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Küldés
          </Button>
        </form>
      </section>
    </div>
  );
}

const Stat = ({ label, value, mono }) => (
  <div className="rounded-xl border border-white/60 bg-white/60 px-3 py-2 backdrop-blur-sm">
    <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-amber-800/70">{label}</div>
    <div className={`mt-0.5 text-sm font-semibold text-slate-900 ${mono ? "tabular-nums" : ""}`}>{value || "—"}</div>
  </div>
);
